/** @jsx jsx */
/** @jsxRuntime classic */
import { Interpolation, jsx } from '@emotion/react';
import BuildingIcon from './Building';
import Caret from './Caret';
import CheckmarkCircle from './CheckmarkCircle';
import ChevronLeftIcon from './ChevronLeft';
import ChevronRightIcon from './ChevronRight';
import ClearIcon from './Clear';
import ConnectorIcon from './Connector';
import DollarIcon from './Dollar';
import { EyeOpenedIcon, EyeClosedIcon } from './Eye';
import PasswordLockIcon from './PasswordLock';
import PinwheelLogo from './PinwheelLogo';
import SearchIcon from './Search';
import XIcon from './X';
import ShieldIcon from './Shield';
import CheckmarkOutsideCircleIcon from './CheckmarkOutsideCircle';
import { ThemeConfig } from '../style/theme';
import IncognitoIcon from './Incognito';
import LockIcon from './Lock';
import PassInfoIcon from './PassInfo';
import PieChartIcon from './PieChart';
import Step1Icon from './Step1Icon';
import Step2Icon from './Step2Icon';
import Step3Icon from './Step3Icon';
import ThumbsUpIcon from './ThumbsUp';
import GenericPayrollProviderIcon from './GenericPayrollProvider';

export const ICON_NAMES = [
  'building',
  'caret',
  'clear',
  'checkmarkCircle',
  'checkmarkOutsideCircle',
  'chevronLeft',
  'chevronRight',
  'connector',
  'dollar',
  'eyeClosed',
  'eyeOpened',
  'genericPayrollProvider',
  'passordLock',
  'pinwheelLogo',
  'search',
  'shield',
  'x',
  'incognito',
  'lock',
  'passInfo',
  'pieChart',
  'step1',
  'step2',
  'step3',
  'thumbsUp',
] as const;

export type IconName = typeof ICON_NAMES[number];

export type CommonIconProps = {
  color?: string;
  className?: string;
  viewBox?: string;
};

export type Props = CommonIconProps & {
  size?: number;
  icon: IconName;
  sx?: Interpolation<ThemeConfig>;
};

const ICON_MAP: {
  [K in IconName]: ({ color }: CommonIconProps) => JSX.Element;
} = {
  building: BuildingIcon,
  caret: Caret,
  checkmarkCircle: CheckmarkCircle,
  checkmarkOutsideCircle: CheckmarkOutsideCircleIcon,
  chevronLeft: ChevronLeftIcon,
  chevronRight: ChevronRightIcon,
  clear: ClearIcon,
  connector: ConnectorIcon,
  dollar: DollarIcon,
  eyeClosed: EyeClosedIcon,
  eyeOpened: EyeOpenedIcon,
  genericPayrollProvider: GenericPayrollProviderIcon,
  passordLock: PasswordLockIcon,
  pinwheelLogo: PinwheelLogo,
  search: SearchIcon,
  shield: ShieldIcon,
  x: XIcon,
  incognito: IncognitoIcon,
  lock: LockIcon,
  passInfo: PassInfoIcon,
  pieChart: PieChartIcon,
  step1: Step1Icon,
  step2: Step2Icon,
  step3: Step3Icon,
  thumbsUp: ThumbsUpIcon,
};

const Icon = ({
  size = 24,
  color = '#4D5E73',
  icon,
  className,
  sx,
  viewBox = `0 0 24 24`,
}: Props): JSX.Element => {
  const El = ICON_MAP[icon];
  return (
    <svg
      data-testid={`Icon-${icon}`}
      className={className}
      css={sx}
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...El.prototype.svgPropOverrides}
    >
      <El color={color} />
    </svg>
  );
};

export default Icon;
